import React, { useMemo, useState, useEffect } from 'react';
import { localized } from '../i18n/translations';
import { useParams, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import NotifyAvailable from "../components/NotifyAvailable";
import CustomImage from '../components/CustomImage';
import SEO from '../components/SEO';

function Collection({ cartItems, productsData, ChangeQty, addToCart, currency, country, translate, categories }) {
  const { collectionUrl } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [notifyProductId, setNotifyProductId] = useState(null);
  const [notifyCategoryId, setNotifyCategoryId] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  // Countdown state for unpublished collections
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    finished: false
  });

  // Countdown state for "Dostupno do" (available until) when collection is live
  const [availableUntilCountdown, setAvailableUntilCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    finished: false
  });
  
  // Find category where is_collection is true and collection_url matches
  // Handle both boolean true and numeric 1 from database
  const collection = categories.find(cat =>
    (cat.is_collection === true || cat.is_collection === 1 || cat.is_main_collection === true || cat.is_main_collection === 1) &&
    cat.collection_url &&
    cat.collection_url.toLowerCase() === collectionUrl.toLowerCase()
  );

  
  // Get countdown date from collection, or use default if not set
  // Treat the date as UTC to match backend timezone
  const countdownDate = collection?.countdown_date ? (() => {
    // Parse the date string and treat it as UTC
    const dateStr = collection.countdown_date;
    // If it doesn't have timezone info, append 'Z' to indicate UTC
    const utcDateStr = dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-', 10) 
      ? dateStr 
      : dateStr + 'Z';
    return new Date(utcDateStr).getTime();
  })() : null;
  
  // Update countdown timer
  useEffect(() => {
    if (!countdownDate) {
      // No countdown date set, consider it finished
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
      return;
    }
    
    let wasFinished = false;
    
    const updateCountdown = () => {
      // Use UTC time to match backend
      const now = new Date().getTime();
      const distance = countdownDate - now;
      
      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
        
        // Dispatch event when countdown first finishes to trigger data refetch
        if (!wasFinished) {
          wasFinished = true;
          window.dispatchEvent(new CustomEvent('countdownFinished'));
        }
        return;
      }
      
      wasFinished = false;
      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
        finished: false
      });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [countdownDate]);

  // Parse "available until" date (same as countdown_date)
  const availableUntilDateTs = collection?.available_until_date ? (() => {
    const dateStr = collection.available_until_date;
    const utcDateStr = dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-', 10)
      ? dateStr
      : dateStr + 'Z';
    return new Date(utcDateStr).getTime();
  })() : null;

  useEffect(() => {
    if (!availableUntilDateTs) {
      setAvailableUntilCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
      return;
    }
    const update = () => {
      const now = new Date().getTime();
      const distance = availableUntilDateTs - now;
      if (distance < 0) {
        setAvailableUntilCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
        return;
      }
      setAvailableUntilCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
        finished: false
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [availableUntilDateTs]);

  const closeModal = () => {
    setShowModal(false);
  };
  
  
  const collectionId = collection?.id;
  
  // Check for preview key
  const urlParams = new URLSearchParams(window.location.search);
  const previewKey = urlParams.get('key') || localStorage.getItem('previewKey') || '';
  const isPreviewMode = !!previewKey;
  
  // Determine if we should show products:
  // 1. If countdown_date exists and hasn't passed: don't show products (show countdown) - unless preview mode
  // 2. If countdown_date doesn't exist OR has passed: show products only if is_collection_listed is true - unless preview mode
  const shouldShowCountdown = !isPreviewMode && collection?.countdown_date && !countdown.finished;
  const shouldShowProducts = isPreviewMode || (!shouldShowCountdown && collection && (collection.is_collection_listed === true || collection.is_collection_listed === 1));
  const productsInCollection = (shouldShowProducts && collectionId) ? productsData.filter(product => product.category === collectionId) : [];

  // All products grouped by category (always shown, even during countdown)
  // Excludes products belonging to the current collection
  const allProductsByCategory = useMemo(() => {
    if (!productsData || !Array.isArray(productsData)) return [];
    const regular = productsData.filter(p => !p.partnership_atelje && p.category !== collectionId);
    const grouped = {};
    regular.forEach(p => {
      if (!grouped[p.category]) grouped[p.category] = [];
      grouped[p.category].push(p);
    });
    return categories
      .filter(cat => grouped[cat.id] && grouped[cat.id].length > 0)
      .map(cat => ({ category: cat, products: grouped[cat.id] }));
  }, [productsData, categories]);

  const renderProduct = (product, category) => {
    const link = previewKey
      ? `/proizvod/${category?.path || 'default'}/${product.path}?key=${previewKey}`
      : `/proizvod/${category?.path || 'default'}/${product.path}`;
    return (
      <div className="group" key={product.id}>
        <Link to={link} className="block">
          <div className="relative bg-white">
            {product.is_on_sale && product.sale_discount_percentage > 0 && (
              <div className="absolute top-2 right-2 z-10">
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  -{product.sale_discount_percentage}%
                </span>
              </div>
            )}
            {product.custom_tag ? (
              <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">{product.custom_tag}</span>
              </div>
            ) : (
              ((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
                <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                  <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">{translate("Sold out")}</span>
                </div>
              )
            )}
            <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
              <CustomImage src={product.imageUrl} alt={product.imageAlt} className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="p-2">
              <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
                {localized(product, 'name', country)}
              </h3>
              {product.is_on_sale && product.sale_discount_percentage > 0 ? (
                <div className="flex items-center gap-2">
                  <span className="text-brownSkin font-medium">{Number(product[`discounted_${currency}`] || 0).toFixed(2)} {currency}</span>
                  <span className="text-sm text-gray-500 line-through">{Number(product[currency] || 0).toFixed(2)} {currency}</span>
                </div>
              ) : (
                <span className="text-brownSkin font-medium">{Number(product[currency] || 0).toFixed(2)} {currency}</span>
              )}
            </div>
          </div>
        </Link>
      </div>
    );
  };

  // Sort products based on sortBy
  const sortedProducts = useMemo(() => {
    if (!productsInCollection || productsInCollection.length === 0) return [];
    
    let sorted = [...productsInCollection];
    
    if (sortBy === 'price-low') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceB - priceA;
      });
    } else if (sortBy === 'date-new') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'date-old') {
      return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    return sorted;
  }, [productsInCollection, sortBy, currency]);

  const productsItems = sortedProducts.map(product => {
    const category = categories.find(cat => cat.id === product.category);
    const productLink = previewKey 
      ? `/proizvod/${category?.path || collection?.path || 'default'}/${product.path}?key=${previewKey}`
      : `/proizvod/${category?.path || collection?.path || 'default'}/${product.path}`;
    
    return (
      <div className="group" key={product.id}>
        <Link to={productLink} className="block">
          <div className="relative bg-white">
            {/* Sale Badge */}
            {product.is_on_sale && product.sale_discount_percentage > 0 && (
              <div className="absolute top-2 right-2 z-10">
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  -{product.sale_discount_percentage}%
                </span>
              </div>
            )}
            {/* Custom Tag Badge or Rasprodato Badge (show only one) */}
            {product.custom_tag ? (
              <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  {product.custom_tag}
                </span>
              </div>
            ) : (
              ((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
                <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                  <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {translate("Sold out")}
                  </span>
                </div>
              )
            )}
            <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
              <CustomImage 
                src={product.imageUrl} 
                alt={product.imageAlt} 
                className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
              />
            </div>
            <div className="p-2">
              <div>
                <Link to={`/proizvod/${category?.path || collection?.path || 'default'}/${product.path}`} className="no-underline text-brownSkin inline-block">
                  <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
                    {localized(product, 'name', country)}
                  </h3>
                </Link>
                <div className="product-card__price-badge">
                  <div className="product-price">
                    {product.is_on_sale && product.sale_discount_percentage > 0 ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-brownSkin font-medium">
                            {Number(product[`discounted_${currency}`] || 0).toFixed(2)} {currency}
                          </span>
                          <span className="text-sm text-gray-500 line-through">
                            {Number(product[currency] || 0).toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-brownSkin font-medium">
                          {Number(product[currency] || 0).toFixed(2)} {currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  });

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Show loading state while categories are being loaded
  if (!categories || categories.length === 0) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brownSkin mb-4"></div>
            <p className="text-gray-600">{translate("Loading collection...")}</p>
          </div>
        </div>
        <Footer categories={categories || []} />
      </div>
    );
  }

  // Only show "not found" after categories have loaded
  if (!collection) {
    return (
      <div className="bg-white min-h-screen">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🏺</div>
          <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("Collection not found")}</h3>
          <p className="text-gray-600 mb-8">{translate('Collection "{name}" does not exist.').replace('{name}', collectionUrl)}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
          >
            {translate("Back to home")}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
          </Link>
        </div>
        <Footer categories={categories} />
      </div>
    );
  }

  const collectionTitle = collection.collection_name || collection.titleInCategory || collection.title;

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={`${collectionTitle} - Bulka Ceramics`}
        description={collection.descriptionInCategory || collection.description}
        type="website"
        name="Bulka Ceramics"
      />

      {showModal && <NotifyAvailable onClose={closeModal} notifyProductId={notifyProductId} notifyCategoryId={notifyCategoryId} country={country} />}

      {/* Show countdown and mystery products if countdown not finished (unless preview mode), otherwise show actual products if published */}
      {shouldShowCountdown && !isPreviewMode ? (
        /* Countdown Display with 8 Mystery Products */
        <div className="bg-bgSkin pt-16 pb-12">
          <div className="w-full mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-12">
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                  {collectionTitle}
                </h2>
                <div className="w-24 h-1 bg-darkerSkin mx-auto rounded-full"></div>
              </div>
            </div>
            
            <div className="flex flex-col items-center justify-center py-12">
              {/* Countdown Section - Above Mystery Items */}
              <div className="w-full mb-12">
                <div className="text-center mb-8">
                  <p className="text-xl md:text-2xl text-gray-700 mb-6">
                    {translate("New collection coming soon!")}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 w-full max-w-4xl mx-auto">
                  {/* Days */}
                  <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                      {countdown.days}
                    </div>
                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                      {translate("Days")}
                    </div>
                  </div>
                  {/* Hours */}
                  <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                      {countdown.hours}
                    </div>
                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                      {translate("Hours")}
                    </div>
                  </div>
                  {/* Minutes */}
                  <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                      {countdown.minutes}
                    </div>
                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                      {translate("Minutes")}
                    </div>
                  </div>
                  {/* Seconds */}
                  <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                    <div className="text-4xl md:text-5xl font-bold text-brownSkin mb-2">
                      {countdown.seconds}
                    </div>
                    <div className="text-sm md:text-base text-gray-600 uppercase tracking-wide">
                      {translate("Seconds")}
                    </div>
                  </div>
                </div>
                {/* Notify Me Button */}
                <div className="text-center mt-8">
                  {collection.countdown_date && (
                    <button
                      onClick={() => { setShowModal(true); setNotifyProductId(null); setNotifyCategoryId(collection?.id || null); }}
                      className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-medium bg-brownSkin text-white shadow-lg hover:shadow-xl hover:bg-brownSkin/90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brownSkin/40 focus:ring-offset-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0h6z"/>
                      </svg>
                      {translate("Notify me when available")}
                    </button>
                  )}
                </div>
              </div>

              {/* Mystery Products Preview - 8 items */}
              <div className="w-full">
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="group bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl border-2 border-dashed border-gray-300">
                      {/* Mystery Image Container */}
                      <div className="relative overflow-hidden aspect-square bg-gradient-to-br from-gray-100 to-gray-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          {/* Question Mark Icon */}
                          <div className="w-24 h-24 bg-gray-400 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                          </div>
                        </div>
                        {/* Coming Soon Overlay */}
                        <div className="absolute inset-0 bg-brownSkin/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>

                      {/* Mystery Product Info */}
                      <div className="p-4">
                        <div className="text-center">
                          <h3 className="font-semibold text-gray-600 mb-2">
                            {translate("Coming soon")}
                          </h3>
                          <p className="text-sm text-gray-500 mb-3">
                            {collectionTitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
          /* Published - Show Actual Products */
          <>
            {/* Dostupno do banner + countdown (above products) */}
            {collection?.available_until_date && !availableUntilCountdown.finished && (
              <div className="bg-bgSkin/80 border-b border-brownSkin/20">
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 pt-24">
                  <div className="text-center mb-4">
                    <p className="text-lg md:text-xl text-brownSkin font-medium">
                      {translate("Limited edition. Available until {date} or while supplies last.").replace('{date}', availableUntilDateTs ? (() => {
                        const months = ['januara','februara','marta','aprila','maja','juna','jula','augusta','septembra','oktobra','novembra','decembra'];
                        const d = new Date(availableUntilDateTs);
                        return `${d.getUTCDate()}. ${months[d.getUTCMonth()]} `;
                      })() : '')}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 w-full max-w-3xl mx-auto">
                    <div className="bg-white rounded-lg shadow p-4 text-center">
                      <div className="text-2xl md:text-3xl font-bold text-brownSkin">{availableUntilCountdown.days}</div>
                      <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide">{translate("Days")}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 text-center">
                      <div className="text-2xl md:text-3xl font-bold text-brownSkin">{availableUntilCountdown.hours}</div>
                      <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide">{translate("Hours")}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 text-center">
                      <div className="text-2xl md:text-3xl font-bold text-brownSkin">{availableUntilCountdown.minutes}</div>
                      <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide">{translate("Minutes")}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 text-center">
                      <div className="text-2xl md:text-3xl font-bold text-brownSkin">{availableUntilCountdown.seconds}</div>
                      <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wide">{translate("Seconds")}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sorting Controls */}
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-brownSkin font-medium">
                  {translate("Showing {count} products").replace('{count}', sortedProducts.length)}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="SortBy" className="text-brownSkin font-medium text-sm">{translate("Sort by:")}</label>
                  </div>

                  {/* Sort Select */}
                  <div className="relative">
                    <select
                      name="sort_by"
                      id="SortBy"
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="appearance-none bg-white border border-brownSkin/30 rounded-md px-4 py-2 pr-10 focus:ring-brownSkin focus:border-brownSkin focus:ring-1 text-sm min-w-[200px] text-brownSkin"
                    >
                      <option value="default">{translate("Featured")}</option>
                      <option value="price-low">{translate("Price, low to high")}</option>
                      <option value="price-high">{translate("Price, high to low")}</option>
                      <option value="date-old">{translate("Date, oldest to newest")}</option>
                      <option value="date-new">{translate("Date, newest to oldest")}</option>
                    </select>
                    <svg className="w-5 h-5 text-brownSkin absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
              {/* Products Header */}
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                  {collectionTitle}
                </h2>
                <div className="w-24 h-1 bg-brownSkin rounded-full mx-auto mb-6"></div>
              </div>
              
              {sortedProducts.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
                  {productsItems}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">🏺</div>
                  <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("No products in this collection")}</h3>
                  <p className="text-gray-600 mb-8">{translate("No products currently available to display.")}</p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
                  >
                    {translate("Back to home")}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                    </svg>
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

      {/* All products grouped by category — always visible, excludes current collection */}
      {allProductsByCategory.length > 0 && (
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="space-y-16">
            {allProductsByCategory.map(({ category, products }) => (
              <div key={category.id}>
                <div className="mb-6">
                  <Link to={`/kategorija/${category.path}`} className="inline-flex items-center gap-2 group">
                    <h3 className="text-xl md:text-2xl font-bold text-brownSkin group-hover:text-brownSkin/80 transition-colors">
                      {category.collection_name || category.titleInCategory || category.title}
                    </h3>
                    <svg className="w-4 h-4 text-brownSkin/50 group-hover:text-brownSkin transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </Link>
                  <div className="w-10 h-0.5 bg-brownSkin/30 mt-2"></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
                  {products.map(product => renderProduct(product, category))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Footer categories={categories} />
    </div>
  );
}

export default Collection;
